/**
 * Cierre de caja.
 *
 * El backend bloquea el cierre si quedan órdenes sin cobrar y devuelve cuáles.
 * Esta pantalla se adelanta a eso: pide `/turnos/pendientes` al abrirse y, si
 * hay mesas colgadas, muestra la lista y deshabilita el botón. Descubrir el
 * bloqueo al intentar cerrar —a la medianoche, con el local vacío— sería la
 * peor forma de enterarse.
 *
 * La diferencia se previsualiza mientras el cajero cuenta, pero la cifra que
 * vale es la que calcula Postgres al cerrar: acá se muestra, allá se registra.
 */

import { useEffect, useState } from 'react';
import * as api from '../api/endpoints';
import { ErrorApi, ErrorRed } from '../api/cliente';
import { cop, hhmm } from '../lib/formato';
import type { Arqueo, OrdenPendiente, RespuestaTurnoActual } from '../tipos/contrato';

interface Props {
  actual: RespuestaTurnoActual;
  onCerrado: (arqueo: Arqueo) => void;
  onCerrar: () => void;
}

const ETIQUETA_ESTADO: Record<Arqueo['estado'], string> = {
  cuadrado: 'La caja cuadra',
  faltante: 'Falta efectivo',
  sobrante: 'Sobra efectivo',
};

export function ModalCierreCaja({ actual, onCerrado, onCerrar }: Props) {
  const [contado, setContado] = useState('');
  const [notas, setNotas] = useState('');
  const [pendientes, setPendientes] = useState<OrdenPendiente[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cerrando, setCerrando] = useState(false);
  const [arqueo, setArqueo] = useState<Arqueo | null>(null);

  const turno = actual.turno;
  const resumen = actual.resumen;
  const esperado = Number(resumen?.efectivoEsperado ?? 0);
  const contadoNum = Number(contado) || 0;
  const diferencia = contadoNum - esperado;

  useEffect(() => {
    void api
      .ordenesPendientes()
      .then((r) => setPendientes(r.pendientes))
      .catch(() => setPendientes(null));
  }, []);

  const bloqueado = (pendientes?.length ?? 0) > 0 || actual.puedeCerrar === false;

  const cerrar = async () => {
    if (!turno) return;
    setError(null);
    setCerrando(true);
    try {
      const r = await api.cerrarTurno(turno.id, contadoNum, notas.trim() || undefined);
      setArqueo(r.arqueo);
    } catch (e) {
      if (e instanceof ErrorRed) setError('Sin conexión: el cierre de caja necesita el servidor.');
      else if (e instanceof ErrorApi) setError(e.message);
      else setError('No se pudo cerrar la caja.');
    } finally {
      setCerrando(false);
    }
  };

  /* ── Resultado del arqueo ───────────────────────────────────────────── */
  if (arqueo) {
    const cuadra = arqueo.estado === 'cuadrado';
    return (
      <div className="overlay on">
        <div className="modal sm" role="dialog" aria-modal="true" style={{ maxWidth: 430 }}>
          <header>
            <h2>Caja cerrada</h2>
          </header>
          <div className="body">
            <div className="amountbig">
              <div className="eyebrow">{ETIQUETA_ESTADO[arqueo.estado]}</div>
              <div className="v mono" style={cuadra ? undefined : { color: 'var(--accent-600)' }}>
                {cop(Math.abs(Number(arqueo.diferencia)))}
              </div>
            </div>
            <div className="paybox">
              <div className="stat">
                <span>Base inicial</span>
                <b className="mono">{cop(arqueo.baseInicial)}</b>
              </div>
              <div className="stat">
                <span>Ventas en efectivo</span>
                <b className="mono">{cop(arqueo.ventasEnEfectivo)}</b>
              </div>
              <div className="stat">
                <span>Efectivo esperado</span>
                <b className="mono">{cop(arqueo.efectivoEsperado)}</b>
              </div>
              <div className="stat">
                <span>Efectivo contado</span>
                <b className="mono">{cop(arqueo.efectivoContado)}</b>
              </div>
            </div>
          </div>
          <footer>
            <div style={{ flex: 1 }} />
            <button className="btn btn-primary" onClick={() => onCerrado(arqueo)}>
              Listo
            </button>
          </footer>
        </div>
      </div>
    );
  }

  /* ── Formulario de cierre ───────────────────────────────────────────── */
  return (
    <div className="overlay on" onMouseDown={(e) => e.target === e.currentTarget && onCerrar()}>
      <div className="modal sm" role="dialog" aria-modal="true" style={{ maxWidth: 470 }}>
        <header>
          <h2>Cerrar caja</h2>
          <span className="mono" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            Turno #{turno?.numero} · desde {hhmm(turno?.abiertoEn)}
          </span>
          <button className="close" onClick={onCerrar} aria-label="Cerrar">
            ✕
          </button>
        </header>

        <div className="body">
          {error && <div className="error-caja">{error}</div>}

          {bloqueado && (
            <div className="error-caja">
              <b>No se puede cerrar todavía.</b> Hay {pendientes?.length ?? '—'} orden(es) sin
              cobrar:
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {(pendientes ?? []).map((o) => (
                  <li key={o.id}>
                    Mesa {o.mesaNumero ?? '—'} · #{o.numero} · {cop(o.total)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="paybox" style={{ marginTop: 0 }}>
            <div className="stat">
              <span>Órdenes cobradas</span>
              <b className="mono">{resumen?.ordenesPagadas ?? 0}</b>
            </div>
            <div className="stat">
              <span>Total vendido</span>
              <b className="mono">{cop(resumen?.totalVendido)}</b>
            </div>
            <div className="stat">
              <span>Propinas</span>
              <b className="mono">{cop(resumen?.totalPropinas)}</b>
            </div>
            <div className="stat">
              <span>Tarjeta</span>
              <b className="mono">{cop(resumen?.totalTarjeta)}</b>
            </div>
            <div className="stat">
              <span>Nequi / Daviplata</span>
              <b className="mono">{cop(resumen?.totalDigital)}</b>
            </div>
            <div
              className="stat"
              style={{ borderTop: '1px solid var(--line)', paddingTop: 8, marginTop: 4 }}
            >
              <span>Efectivo esperado en el cajón</span>
              <b className="mono">{cop(esperado)}</b>
            </div>
          </div>

          <div className="field" style={{ marginTop: 14 }}>
            <label htmlFor="contado">Efectivo contado</label>
            <input
              id="contado"
              className="mono"
              inputMode="numeric"
              autoFocus
              value={contado}
              onChange={(e) => setContado(e.target.value.replace(/\D/g, '').slice(0, 9))}
              placeholder="0"
              style={{ fontSize: 20, fontWeight: 700 }}
            />
          </div>

          <div className="field">
            <label htmlFor="notasCierre">Notas del cierre (opcional)</label>
            <input
              id="notasCierre"
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              placeholder="Ej. se pagó un domicilio de la caja"
            />
          </div>

          {contado !== '' && (
            <div className={`change${diferencia < 0 ? ' short' : ''}`}>
              <span className="l">
                {diferencia === 0 ? 'Cuadra' : diferencia < 0 ? 'Faltante' : 'Sobrante'}
              </span>
              <span className="v mono">{cop(Math.abs(diferencia))}</span>
            </div>
          )}
        </div>

        <footer>
          <button className="btn btn-ghost" onClick={onCerrar}>
            Cancelar
          </button>
          <div style={{ flex: 1 }} />
          <button
            className="btn btn-primary"
            disabled={cerrando || bloqueado || contado === ''}
            onClick={() => void cerrar()}
          >
            {cerrando ? 'Cerrando…' : 'Cerrar caja'}
          </button>
        </footer>
      </div>
    </div>
  );
}
